import React, { FC, useEffect, useRef } from 'react';
import ReactModal from 'react-modal';
import classNames from 'classnames';
import getClassNames from 'src/common/utils/getClassNames';
import Controller from './Controller';
import Body from './Body';
import Footer from './Footer';
import Header from './Header';
import styles from './Modal.module.scss';

export type ModalPosition = 'top' | 'middle' | 'right';
const sizes = ['small', 'medium', 'large', 'x-large'] as const;
export type Size = typeof sizes[number];

export type ModalProps = {
  children: React.ReactNode;
  modalOpen: boolean;
  closeModal: () => void;
  onAfterOpen?: () => void;
  label: string;
  size?: Size;
  position?: ModalPosition;
  mobileFullScreen?: boolean;
  closeTimeoutMS?: number;
  withFocusOutline?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  shouldCloseOnEsc?: boolean;
};

export type CompoundModal = FC<ModalProps> & {
  BodyDS22: typeof Body;
  ControllerDS22: typeof Controller;
  HeaderDS22: typeof Header;
  FooterDS22: typeof Footer;
};

const Modal: CompoundModal = ({
  modalOpen,
  closeModal,
  label,
  size = 'large',
  position = 'top',
  mobileFullScreen,
  children,
  onAfterOpen,
  closeTimeoutMS,
  withFocusOutline = true,
  shouldCloseOnOverlayClick = true,
  shouldCloseOnEsc = true,
}) => {
  const ready = useRef(false);
  useEffect(() => {
    /**
     * If modalOpen={true} on the server render, ReactModal console logs:
     * "Cannot register modal instance that's already open". Subsequently, when closing the modal,
     * ReactModal fails to remove the `body.ReactModal__Body--open` class (preventing scroll).
     * Modals with modalOpen={false} on SSR do not need this, but are not harmed by it either.
     */
    if (typeof window !== 'undefined') {
      ReactModal.setAppElement('#__next');
      ready.current = true;
    }
  }, []);
  return (
    <ReactModal
      // fix react 18 issue where react modal body class isn't removed on unmount
      // https://github.com/reactjs/react-modal/issues/808
      isOpen={modalOpen && ready.current}
      contentLabel={label}
      onAfterOpen={onAfterOpen}
      onRequestClose={closeModal}
      className={getClassNames(styles, 'root', classNames(position, size, { mobileFullScreen, hideFocusOutline: !withFocusOutline }))}
      overlayClassName={getClassNames(styles, 'overlay', classNames(position))}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldCloseOnEsc={shouldCloseOnEsc}
      closeTimeoutMS={closeTimeoutMS}
    >
      {children}
    </ReactModal>
  );
};

Modal.BodyDS22 = Body;
Modal.ControllerDS22 = Controller;
Modal.FooterDS22 = Footer;
Modal.HeaderDS22 = Header;

export default Modal;
